import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Alert, Button, CircularProgress } from "@mui/material";
import { useOrderDraft } from "../context/OrderDraftContext";
import { useCart } from "../context/CartContext";
import { useCurrency } from "../context/CurrencyContext";
import { QuoteController } from "../controllers/quote";
import QtyStepper from "../components/QtyStepper";
import { WAREHOUSES } from "../lib/mockInventory";
import { availableForSku } from "../lib/stockGuard";
import styles from "./OrderReview.module.css";

// Review the draft built on /order-sheet. Lines are grouped by warehouse
// so the customer sees clearly what's shipping out of US vs International.
// Submission creates a Quote (estimate) — same path used by /cart, since
// the B2B flow is quote-first by design.
export default function OrderReview() {
  const { lines, setQty, totalQty, totalAmount, clear } = useOrderDraft();
  const { setItems: setCartItems } = useCart();
  const { formatAmount } = useCurrency();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const nav = useNavigate();

  const grouped = useMemo(() => {
    const m = new Map();
    for (const l of lines) {
      if (!m.has(l.warehouse)) m.set(l.warehouse, []);
      m.get(l.warehouse).push(l);
    }
    return m;
  }, [lines]);

  const overStockLines = useMemo(
    () => lines.filter((l) => {
      const stock = availableForSku(l.sku, l.warehouse);
      return stock != null && l.qty > stock;
    }),
    [lines],
  );

  async function submit() {
    if (!lines.length) return;
    if (overStockLines.length) {
      setError(`${overStockLines.length} line${overStockLines.length === 1 ? "" : "s"} exceed available stock. Reduce quantities to continue.`);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        items: lines.map((l) => ({
          sku: l.sku,
          qty: l.qty,
          unitPrice: l.unitPrice,
          warehouse: l.warehouse,
        })),
        meta: { source: "order-sheet" },
      };
      const q = await QuoteController.create(payload);
      clear();
      if (q?.id) {
        nav(`/quotes/${q.id}`);
      } else {
        // Fallback: send to cart. setItems may not exist in all contexts.
        if (typeof setCartItems === "function") {
          setCartItems(lines.map((l) => ({ sku: l.sku, qty: l.qty, unitPrice: l.unitPrice })));
        }
        nav("/cart");
      }
    } catch (e) {
      setError(e?.message || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (!lines.length) {
    return (
      <div className={styles.page}>
        <h1 className={styles.title}>Review Order</h1>
        <p className={styles.empty}>
          Your order draft is empty. Head to the{" "}
          <Link to="/order-sheet">Order Sheet</Link> to start.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <Link to="/order-sheet" className={styles.back}>← Back to Order Sheet</Link>
        <h1 className={styles.title}>Review Order</h1>
        <p className={styles.lede}>
          {totalQty} unit{totalQty === 1 ? "" : "s"} across {grouped.size} warehouse{grouped.size === 1 ? "" : "s"}.
          Adjust quantities or remove lines before submitting.
        </p>
      </header>

      {WAREHOUSES.map((w) => {
        const items = grouped.get(w.id);
        if (!items || !items.length) return null;
        const subtotal = items.reduce((n, l) => n + l.unitPrice * l.qty, 0);
        const subQty = items.reduce((n, l) => n + l.qty, 0);
        return (
          <section key={w.id} className={styles.group}>
            <header className={styles.groupHead}>
              <h2 className={styles.groupTitle}>
                <span className={styles.flag}>{w.flag}</span> {w.label}
              </h2>
              <div className={styles.groupTotals}>
                {subQty} unit{subQty === 1 ? "" : "s"} · {formatAmount(subtotal)}
              </div>
            </header>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Item</th>
                  <th className={styles.colCenter}>Qty</th>
                  <th className={styles.colRight}>Unit</th>
                  <th className={styles.colRight}>Line</th>
                  <th aria-label="actions" />
                </tr>
              </thead>
              <tbody>
                {items.map((l) => {
                  const stock = availableForSku(l.sku, l.warehouse);
                  const overStock = stock != null && l.qty > stock;
                  return (
                  <tr key={l.key}>
                    <td>
                      <div className={styles.itemCell}>
                        {l.image && <img src={l.image} alt="" className={styles.thumb} />}
                        <div>
                          <div className={styles.name}>{l.itemName}</div>
                          <div className={styles.sub}>
                            {l.colorName}{l.size ? ` · ${l.size}` : ""}
                          </div>
                          <div className={styles.skuLine}>{l.sku}</div>
                          {overStock && (
                            <div className={styles.stockWarn}>
                              Only {stock} available in {l.warehouseLabel || l.warehouse}.
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className={styles.colCenter}>
                      <QtyStepper
                        value={l.qty}
                        max={stock ?? undefined}
                        size="sm"
                        onChange={(n) => setQty(l.key, n, l)}
                      />
                    </td>
                    <td className={styles.colRight}>{formatAmount(l.unitPrice)}</td>
                    <td className={styles.colRight}>{formatAmount(l.unitPrice * l.qty)}</td>
                    <td className={styles.colRight}>
                      <button
                        type="button"
                        onClick={() => setQty(l.key, 0, l)}
                        className={styles.removeBtn}
                        aria-label={`Remove ${l.itemName}`}
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        );
      })}

      <footer className={styles.foot}>
        <div className={styles.summary}>
          <div className={styles.summaryRow}>
            <span>Total quantity</span>
            <span>{totalQty}</span>
          </div>
          <div className={[styles.summaryRow, styles.summaryTotal].join(" ")}>
            <span>Order total</span>
            <span>{formatAmount(totalAmount)}</span>
          </div>
        </div>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <div className={styles.actions}>
          <Button variant="outlined" onClick={() => nav("/order-sheet")} disabled={submitting}>
            Edit sheet
          </Button>
          <Button
            variant="contained"
            onClick={submit}
            disabled={submitting || !lines.length || overStockLines.length > 0}
            startIcon={submitting ? <CircularProgress size={16} /> : null}
          >
            Submit order
          </Button>
        </div>
      </footer>
    </div>
  );
}
